'use client';

import { useState } from 'react';

import { Layout } from '@/components/Layout';
import TierBanner from '@/components/banners/TierBanner';
import {
	AddButton,
	BackButton,
	ContinueWithGoogleButton,
	NextButton,
	RoundedFilledButton,
} from '@/components/buttons/Buttons';
import SwitchButton from '@/components/buttons/SwitchButton';
import ProgramCard from '@/components/cards/ProgramCard';
import Dialog from '@/components/dialog/Dialog';
import LoadingBlocker from '@/components/dialog/LoadingBlocker';
import DropDownCheckbox from '@/components/form-inputs/DropDownCheckbox';
import PasswordInput from '@/components/form-inputs/PasswordInput';
import { SearchBar } from '@/components/search-bar/SearchBar';
import TabContent from '@/components/tabs/TabContent';
import TabViewContainer from '@/components/tabs/TabViewContainer';
import Lists, { BulletList, ListWithIcon } from '@/components/text/Lists';
import {
	CustomText,
	TextBodyExtraExtraSmall,
	TextBodySmall,
	TextHeadlineExtraLarge,
	TextLink,
} from '@/components/text/Texts';
import LoadingDialog from '@/components/utils/LoadingDialog';
import { CREATE_PROGRAM } from '@/constants/button-labels';
import ConfirmationDialogHeader from '@/modules/confirmation/components/ConfirmationDialogHeader';
import { showConfirmationDialog } from '@/modules/confirmation/engines/confirmation.library';
import {
	showError,
	showInfo,
	showNotification,
	showSuccess,
	showWarning,
} from '@/modules/notification/engine/notification.library';
import Banner from '@/modules/people/components/Banner';
import type { DropdownChangeEvent } from 'primereact/dropdown';
import { TabPanel, TabView } from 'primereact/tabview';
import { useForm } from 'react-hook-form';
import {
	MdAdd,
	MdAddCircle,
	MdCheck,
	MdCloudDownload,
	MdFilterAlt,
	MdOutlineModeEdit,
} from 'react-icons/md';
import ListPeopleScreen from '../people/views/ListPeopleScreen';
import GroupTable from '@/modules/people/components/group/GroupTable';
import { TabHelper } from '@/common/TabHelper';

interface Role {
	name: string;
	code: string;
}

export default function Billing() {
	const [showLoading, setShowLoading] = useState(false);
	const [showLoadingBlocker, setShowLoadingBlocker] = useState(false);
	const [checked, setChecked] = useState(false);
	const [newPassword, setNewPassword] = useState('');
	const [selectedRole, setSelectedRole] = useState<Role | null>(null);

	const { control } = useForm();

	const roles: Role[] = [
		{ name: 'None', code: '' },
		{ name: 'Admin', code: 'admin' },
		{ name: 'Coordinator', code: 'coordinator' },
	];

	const tabs = [
		{
			tabName: 'TAB 1',
			tabContent: <TabContent>CONTENT 1</TabContent>,
		},
		{
			tabName: 'TAB 2',
			tabContent: <TabContent>CONTENT 2</TabContent>,
		},
	];

	const suggestions = ['New York', 'Los Angeles', 'Chicago'];

	const handleSuggestionClick = (suggestion: string) => {
		console.log('Selected:', suggestion);
	};

	const deleteMessage =
		'Are you sure you want to delete this employee? This action cannot be undone.';

	const deleteHeader = 'Delete People';

	const headerTemplate = (
		<ConfirmationDialogHeader message={deleteHeader} severity="warning" />
	);

	const handleDeletePeople = () =>
		showConfirmationDialog(deleteMessage, {
			acceptLabel: 'Delete',
			rejectLabel: 'Cancel',
			header: headerTemplate,
			acceptClassName: '!bg-error',
		});

	return (
		<Layout>
			<div className="flex flex-col space-y-10 pb-5">
				<div className="flex gap-12">
					<div className="flex flex-col gap-5">
						<TextHeadlineExtraLarge
							text="Buttons"
							className="text-primary"
						/>
						<div className="w-96 flex flex-row flex-wrap justify-start gap-4">
							<RoundedFilledButton
								id="billing-edit"
								icon={MdOutlineModeEdit}
								iconSize="24"
								className="secondary"
							/>
							<RoundedFilledButton
								id="billing-delete"
								className="w-full disabled-button"
								label="Resend OTP"
							/>
							<RoundedFilledButton
								id="billing-send"
								label="Send Invite"
							/>
							<RoundedFilledButton
								id="billing-send-disabled"
								label="Send Invite"
								className="w-full text-lg font-poppins"
								disabled
							/>
							<RoundedFilledButton
								id="billing-filter"
								className="with-icon-button"
								icon={MdFilterAlt}
								label="Filter"
								// onClick={() => {}}
								width="110px"
							/>
							<RoundedFilledButton
								id="billing-add"
								className="with-icon-button"
								trailingIcon={MdAdd}
								label="Add"
								// onClick={() => {}}
								width="110px"
							/>
							<RoundedFilledButton
								id="billing-export"
								className="with-icon-button-primary"
								trailingIcon={MdCloudDownload}
								label="Export"
								iconColor="text-onPrimary"
								// onClick={() => {}}
								width="110px"
							/>
							<RoundedFilledButton
								id="billing-add"
								label="Add"
								icon={MdAdd}
							/>
							<AddButton
								label={CREATE_PROGRAM}
								icon={MdAddCircle}
								id="add-program-button"
								iconSize="18px"
								className="w-auto"
							// onClick={}
							/>
							<ContinueWithGoogleButton id="billing-continue-with-google" />
							<BackButton
								id="billing-back"
								label="Back"
								withIcon
							/>
							<NextButton id="billing-next" label="Next" />
							<SwitchButton
								checked={checked}
								setChecked={setChecked}
							/>
						</div>
					</div>
					<div className="flex flex-col gap-5">
						<TextHeadlineExtraLarge
							text="Text"
							className="text-primary"
						/>
						<div className="w-96 flex flex-row flex-wrap justify-start items-center content-center gap-4">
							<TextLink
								text="Text Link"
								className="text-tertiary text-label-large py-2"
							/>
							<TextLink
								className="pr-1 font-poppins leading-5 text-sm text-tertiary underline underline-offset-1"
								text="Text Link Underline"
							/>
							<CustomText
								text="CustomText"
								className="text-tertiary forgot-password-link"
							/>
							<CustomText
								className="font-poppins leading-5 text-sm w-auto pr-1"
								text="CustomText"
							/>
							<CustomText
								className="font-poppins text-[20px] leading-[20px] text-primary70"
								text={'Welcome, Julius'}
							/>
							<TextBodyExtraExtraSmall
								className="block pl-[16px] mt-1 text-error"
								text="Error message"
							/>
							<TextBodySmall
								text="TextBodySmall"
								className="align-middle text-primary"
							/>
							<Lists listItems={['Item 1', 'Item 2']} />
							<BulletList
								icon={''}
								listItems={['Bullet Item 1', 'Bullet Item 2']}
								markerSpacingLeft={''}
							/>
							<ListWithIcon
								listItems={['Bullet Item 1', 'Bullet Item 2']}
								icon={
									<MdCheck size={24} className="text-info" />
								}
								markerSpacingLeft="ml-2"
								textStyle="text-label-medium"
								textColor="text-black"
								gapBetweenIconAndText="gap-[10px]"
							/>
						</div>
						<TextHeadlineExtraLarge
							text="Notifications"
							className="text-primary"
						/>

						<div className="w-96 flex flex-row flex-wrap justify-start items-center content-center gap-4">
							<RoundedFilledButton
								label="Show Success"
								id={''}
								onClick={() => showSuccess('Hello, World!')}
							/>
							<RoundedFilledButton
								label="Show Info"
								id={''}
								onClick={() => showInfo('Hello, World!')}
							/>
							<RoundedFilledButton
								label="Show Warning"
								id={''}
								onClick={() => showWarning('Hello, World!')}
							/>
							<RoundedFilledButton
								label="Show Error"
								id={''}
								onClick={() => showError('Hello, World!')}
							/>
							<RoundedFilledButton
								label="Show Notification"
								id={''}
								onClick={() =>
									showNotification('Hello, World!')
								}
							/>
							<RoundedFilledButton
								label="Show Notification With Action"
								id={''}
								onClick={() =>
									showNotification(
										'Hello, World!',
										undefined,
										{
											name: 'Click here to open another toast',
											handler: () =>
												showSuccess('Another toast'),
										},
									)
								}
							/>
						</div>
					</div>
					<div className="flex flex-col gap-5 p-input-filled">
						<TextHeadlineExtraLarge
							text="Password with Criteria"
							className="text-primary"
						/>
						<PasswordInput
							className="password-form-input w-full"
							control={control}
							feedback
							formName="Password Form"
							id="new-password"
							label="New Password"
							onChange={(e) => setNewPassword(e.target.value)}
							placeholder="Enter new password"
							value={newPassword}
						/>
						<TextHeadlineExtraLarge
							text="Dropdown"
							className="text-primary"
						/>
						<p>Single selection</p>
						<DropDownCheckbox
							className="default-dropdown"
							optionLabel="name"
							options={roles}
							id="role"
							value={selectedRole}
							onChange={(e: DropdownChangeEvent) =>
								setSelectedRole(e.value)
							}
							placeholder="Select a role"
						/>
						<TextHeadlineExtraLarge
							text="Search"
							className="text-primary"
						/>
						<p>Search with results</p>
						<SearchBar
							width="360px"
							height="56px"
							placeholder="Search by name or location"
							containerClassName="bg-surfaceContainerHigh"
							suggestions={suggestions}
							onSuggestionClick={handleSuggestionClick}
						/>
						<TextHeadlineExtraLarge
							text="Date Picker"
							className="text-primary"
						/>
					</div>
				</div>
				<div className="flex gap-12">
					<div className="flex flex-1 flex-col gap-5">
						<TextHeadlineExtraLarge
							text="Banner"
							className="text-primary"
						/>
						<Banner />
						<TierBanner
							image={{
								src: '/images/svg/Loyalty.svg',
								width: 56,
								height: 56,
								alt: 'tier',
							}}
							className=" bg-warning99 mb-4 w-1/2"
							content={
								<div>
									<h1 className="text-label-large font-medium">
										Gold Subscription
									</h1>
									<p className="text-label-x-small">
										Our premium pax. Please be warm and
										cordial to them
									</p>
								</div>
							}
						/>
					</div>
					<div className="flex flex-1 flex-col w-full gap-5">
						<TextHeadlineExtraLarge
							text="Tabs"
							className="text-primary"
						/>
						<TabViewContainer tabList={tabs} />
					</div>
				</div>
				<div className="flex justify-start gap-12">
					<div className="flex flex-col w-full">
						<TextHeadlineExtraLarge
							text="Table"
							className="text-primary"
						/>

						<TabView
							className="mt-6"
							pt={TabHelper.TabViewProps}
						>
							<TabPanel
								pt={TabHelper.TabPanelProps}
								header="People"
							>
								<ListPeopleScreen />
							</TabPanel>
							<TabPanel
								pt={TabHelper.TabPanelProps}
								header="Group"
							>
								<GroupTable />
							</TabPanel>
						</TabView>
					</div>
				</div>

				<div className="flex justify-start gap-12">
					<div className="flex flex-col gap-5">
						<TextHeadlineExtraLarge
							text="Dialog Loading"
							className="text-primary"
						/>
						<p>set to true to see dialog loading</p>
						<div className="w-60">
							<RoundedFilledButton
								id="billing-send"
								label="Show loading dialog"
								onClick={() => {
									setShowLoading(true);

									setTimeout(() => {
										setShowLoading(false);
									}, 3000);
								}}
							/>
						</div>
						<LoadingDialog isLoading={showLoading} />
					</div>
					<div className="flex flex-col gap-5">
						<TextHeadlineExtraLarge
							text="Loading Blocker"
							className="text-primary"
						/>
						<span>set to true to see confirmation dialog</span>
						<div className="w-60">
							<RoundedFilledButton
								id="billing-send"
								label="Show loading dialog"
								onClick={() =>
									setShowLoadingBlocker(!showLoading)
								}
							/>
						</div>
						<Dialog
							visible={showLoadingBlocker}
							onHide={() => setShowLoadingBlocker(false)}
						>
							<LoadingBlocker blocked={showLoadingBlocker}>
								Lorem Ipsum
							</LoadingBlocker>
						</Dialog>
					</div>
					<div className="flex flex-col gap-5">
						<TextHeadlineExtraLarge
							text="Confirmation Dialog"
							className="text-primary"
						/>
						<p>
							set to true to see loading block, use in Dialog
							content
						</p>
						<div className="w-60">
							<RoundedFilledButton
								id="billing-send"
								label="Show confirmation dialog"
								onClick={handleDeletePeople}
							/>
						</div>
					</div>
				</div>

				<div className="flex justify-start gap-12">
					<div className="flex flex-1 flex-col gap-5">
						<TextHeadlineExtraLarge
							text="Card"
							className="text-primary"
						/>
						<ProgramCard
							imageUrl="/images/svg/blue-car.svg"
							title="Travel Program"
							description="Simplify the process for employees to expense rides directly through the Xpress app. Additionally, gain valuable insights into your team's transportation expenditure"
							checked={checked}
							setChecked={setChecked}
						/>
					</div>
				</div>
			</div>
		</Layout>
	);
}
